import axios from "axios";
import { useRef, useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FaEye,
  FaEyeSlash,
  FaCheckCircle,
  FaExclamationTriangle,
  FaTimesCircle,
  FaInfoCircle,
} from "react-icons/fa";

/*
  AdvisorRegistration (single-file)
  - includes an internal reusable Modal component (Professional look)
  - replaces Swal popups with the modal
  - keeps your validation and flow intact
  - FIX: password icon alignment issue corrected
*/

export default function AdvisorRegistration() {
  const navigate = useNavigate();
  const [data, setData] = useState({
    role: "",
    pan_number: "",
    first_name: "",
    last_name: "",
    email: "",
    country_code: "+91",
    phone: "",
    password: "",
    confirmPassword: "",
    document: null,
    agent_id: "",
    acceptTerms: false,
    confirmIndian: false,
    showPassword: false,
    showConfirmPassword: false,
  });

  const [verify, setVerify] = useState({});
  const [errors, setErrors] = useState({});
  const [showOTP, setShowOTP] = useState(false);
  const [showSetPassword, setShowSetPassword] = useState(false);
  const [otpDigits, setOtpDigits] = useState(["", "", "", ""]);
  const inputRefs = useRef([]);
  const [agentsList, setAgentsList] = useState([]);
  const [loadingAgents, setLoadingAgents] = useState(false);

  // modal state
  const [modal, setModal] = useState({
    open: false,
    variant: "info", // 'success' | 'warning' | 'error' | 'info'
    title: "",
    message: "",
    showCancel: false,
    confirmText: "",
    cancelText: "Cancel",
    onConfirm: null,
    onCancel: null,
  });

  const openModal = (opts, callback) => {
    setModal((m) => ({ ...m, open: true, ...opts }));

    setTimeout(() => {
      closeModal();
      if (typeof callback === "function") {
        callback(); // <-- THIS will move to step 2
      }
    }, 2000); // 2 sec
  };

  const closeModal = () =>
    setModal({
      open: false,
      variant: "info",
      title: "",
      message: "",
      showCancel: false,
      confirmText: "OK",
      cancelText: "Cancel",
      onConfirm: null,
      onCancel: null,
    });

  // Regex and helpers
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
  const numericRegex = /^[0-9]+$/;
  const strongPasswordRegex =
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/;

  const countryCodes = [
    { code: "+91", label: "India (+91)" },
    { code: "+1", label: "USA (+1)" },
    { code: "+44", label: "UK (+44)" },
    { code: "+61", label: "Australia (+61)" },
  ];

  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = "/css/style.css";
    document.head.appendChild(link);
    return () => {
      document.head.removeChild(link);
    };
  }, []);

  // Fetch agents list when role is Client
  useEffect(() => {
    const fetchAgents = async () => {
      if (data.role === "3") {
        setLoadingAgents(true);
        try {
          const res = await axios.get(
            `${process.env.REACT_APP_BACKEND_BASE_URL}/user/agents`
          );
          if (res.data.status === 1) {
            setAgentsList(res.data.data || []);
          }
        } catch (error) {
          console.error("Error fetching agents:", error);
          setAgentsList([]);
        } finally {
          setLoadingAgents(false);
        }
      } else {
        setAgentsList([]);
        setData((prev) => ({ ...prev, agent_id: "" }));
      }
    };
    fetchAgents();
  }, [data.role]);

  // ----------------- Input Handling -----------------
  const handleInputChange = (e) => {
    const { name, value, files, type, checked } = e.target;

    if (type === "file") {
      const file = files && files[0] ? files[0] : null;
      setData((prev) => ({ ...prev, [name]: file }));
      setErrors((prev) => {
        const n = { ...prev };
        delete n[name];
        return n;
      });
      return;
    }

    if (type === "checkbox") {
      setData((prev) => ({ ...prev, [name]: checked }));
      setErrors((prev) => {
        const n = { ...prev };
        delete n[name];
        return n;
      });
      return;
    }

    const newValue = value;

    if (name === "role") {
      setData((prev) => {
        const upd = { ...prev, [name]: newValue };
        if (newValue !== "2") upd.document = null;
        if (newValue !== "3") upd.agent_id = ""; // Clear agent_id if not client
        return upd;
      });
      setErrors((prev) => {
        const n = { ...prev };
        delete n.role;
        delete n.document;
        delete n.agent_id;
        return n;
      });
      return;
    }

    setData((prev) => ({ ...prev, [name]: newValue }));
    setErrors((prev) => {
      const n = { ...prev };
      delete n[name];
      return n;
    });
  };

  // Uppercase PAN on blur and validate
  const handlePanBlur = () => {
    const v = (data.pan_number || "").toUpperCase();
    setData((prev) => ({ ...prev, pan_number: v }));
    const err = validateFieldSingle("pan_number", v);
    setErrors((prev) => ({ ...prev, ...err }));
  };

  // ----------------- Validation helpers -----------------
  const validateFieldSingle = (name, value) => {
    const v = typeof value === "undefined" ? data[name] : value;
    const localErr = {};

    switch (name) {
      case "role":
        if (!v) localErr.role = "Please select account type.";
        break;
      case "pan_number":
        if (!v) localErr.pan_number = "PAN Number is required.";
        else if (!panRegex.test((v || "").toUpperCase()))
          localErr.pan_number = "PAN Number must be in format AAAAA9999A.";
        break;
      case "first_name":
        if (!v || v.trim().length < 2)
          localErr.first_name = "Please enter first name (min 2 chars).";
        break;
      case "last_name":
        if (!v || v.trim().length < 2)
          localErr.last_name = "Please enter last name (min 2 chars).";
        break;
      case "email":
        if (!v) localErr.email = "Email is required.";
        else if (!emailRegex.test((v || "").toLowerCase()))
          localErr.email = "Please enter a valid email address.";
        break;
      case "phone":
        if (!v) localErr.phone = "Phone number is required.";
        else if (!numericRegex.test(v))
          localErr.phone = "Phone number digits only.";
        else {
          const cc = data.country_code || "+91";
          if (cc === "+91" && v.length !== 10) {
            localErr.phone = "For India (+91) phone must be 10 digits.";
          } else if (v.length < 6 || v.length > 15) {
            localErr.phone = "Phone length must be between 6 and 15 digits.";
          }
        }
        break;
      case "document":
        if (data.role === "2" && !v)
          localErr.document = "NISM certificate is required for advisors.";
        break;
      case "password":
        if (!v) localErr.password = "Password is required.";
        else if (!strongPasswordRegex.test(v))
          localErr.password =
            "Password must be min 8 chars, include uppercase, lowercase, digit and special character.";
        break;
      case "confirmPassword":
        if (!v) localErr.confirmPassword = "Please confirm password.";
        else if (v !== data.password)
          localErr.confirmPassword = "Passwords do not match.";
        break;
      default:
    }
    return localErr;
  };

  const handleGlobalEnterKey = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();

      // Step 1
      if (!showOTP && !showSetPassword) {
        handleSendOTP(e);
      }

      // Step 2
      else if (showOTP && !showSetPassword) {
        handleVerifyOTP(e);
      }

      // Step 3
      else if (showSetPassword) {
        handleCreateAccount(e);
      }
    }
  };

  const validateStep1 = () => {
    const localErrors = {};
    const requiredFields = [
      "role",
      "pan_number",
      "first_name",
      "last_name",
      "email",
      "phone",
    ];
    if (data.role === "2") requiredFields.push("document");
    requiredFields.forEach((f) => {
      Object.assign(localErrors, validateFieldSingle(f));
    });
    setErrors((prev) => ({ ...prev, ...localErrors }));
    return Object.keys(localErrors).length === 0;
  };

  const handleOtpKeyDown = (e, index) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      const newOtp = [...otpDigits];
      if (newOtp[index]) {
        newOtp[index] = "";
        setOtpDigits(newOtp);
      } else if (index > 0) {
        inputRefs.current[index - 1]?.focus();
        const updatedOtp = [...otpDigits];
        updatedOtp[index - 1] = "";
        setOtpDigits(updatedOtp);
      }
    }
  };

  // ----------------- API Calls (use modal) -----------------
  const handleSendOTP = async (e) => {
    e.preventDefault();
    if (!validateStep1()) {
      openModal({
        variant: "warning",
        title: "Validation required",
        message:
          "Please fill in all the mandatory details before sending the OTP.",
        showCancel: false,
        confirmText: "",
      });
      return;
    }

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_BASE_URL}/user/reqOTPReg`,
        { email: data.email },
      );
      if (res.status === 200) {
        openModal(
          {
            variant: "success",
            title: "OTP Sent",
            message: "OTP sent successfully. Please check your email.",
          },
          () => {
            setShowOTP(true);
            closeModal();
            window.scrollTo({ top: 0, behavior: "instant" });
          },
        );
      } else if (res.status === 204) {
        openModal({
          variant: "warning",
          title: "Email exists",
          message:
            "This email is already registered. Please use a different email.",
        });
      } else {
        openModal({
          variant: "error",
          title: "Failed",
          message: "Failed to send OTP.",
        });
      }
    } catch (err) {
      console.error(err);
      openModal({
        variant: "error",
        title: "Error",
        message: "Something went wrong while sending OTP.",
      });
    }
  };

  // ✅ Handle OTP Paste (NEW)
const handleOtpPaste = (e, index) => {
  e.preventDefault();

  const pastedData = e.clipboardData
    .getData("text")
    .replace(/\D/g, "")
    .slice(0, otpDigits.length - index);

  if (!pastedData) return;

  const newOtp = [...otpDigits];

  pastedData.split("").forEach((char, i) => {
    newOtp[index + i] = char;
  });

  setOtpDigits(newOtp);
  setVerify({
    email: data.email,
    otp: newOtp.join(""),
  });

  inputRefs.current[index + pastedData.length - 1]?.focus();
};


  const handleOtpDigitChange = (element, index) => {
    const value = element.value;
    if (isNaN(value)) return;

    const newOtp = [...otpDigits];
    newOtp[index] = value;
    setOtpDigits(newOtp);
    setVerify({
      email: data.email,
      otp: newOtp.join(""),
    });

    if (value && index < 3) {
      inputRefs.current[index + 1].focus();
    }
  };
  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_BASE_URL}/user/verifyOTP`,
        verify,
      );
      if (res.status === 200) {
        setShowSetPassword(true);
      } else if (res.status === 205) {
        openModal({ variant: "warning", title: "Invalid OTP" });
      } else {
        openModal({ variant: "warning", title: "OTP Verification Failed" });
      }
    } catch (err) {
      console.error(err);
      openModal({ variant: "error", title: "Something went wrong" });
    }
  };

  const handleCreateAccount = async (e) => {
    e.preventDefault();
    const pErr = {
      ...validateFieldSingle("password", data.password),
      ...validateFieldSingle("confirmPassword", data.confirmPassword),
    };
    if (Object.keys(pErr).length > 0) {
      setErrors((prev) => ({ ...prev, ...pErr }));
      openModal({
        variant: "warning",
        title: "Password Error",
        message: "Please fix password errors.",
      });
      return;
    }

    if (!data.acceptTerms) {
      openModal({
        variant: "warning",
        title: "Terms Required",
        message: "Please accept Terms & Conditions.",
      });
      return;
    }

    try {
      const formData = new FormData();
      formData.append("role", data.role);
      formData.append("pan_number", data.pan_number);
      formData.append("first_name", data.first_name);
      formData.append("last_name", data.last_name);
      formData.append("email", data.email);
      formData.append("phone", data.phone);
      formData.append("country_code", data.country_code);
      formData.append("password", data.password);
      if (data.role === "2" && data.document)
        formData.append("document", data.document);
      if (data.role === "3" && data.agent_id)
        formData.append("agent_id", data.agent_id);

      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_BASE_URL}/user/registrationUser`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );

      if (res.status === 200) {
        openModal({
          variant: "success",
          title: "Account Created",
          message: "Account created successfully.",
        });

        // 🔥 Auto redirect after 1.5 sec
        setTimeout(() => {
          closeModal();
          navigate("/login");
        }, 1500);
      } else {
        openModal({
          variant: "error",
          title: "Failed",
          message: "Failed to create account.",
        });
      }
    } catch (err) {
      console.error(err);
      openModal({
        variant: "error",
        title: "Error",
        message: "Something went wrong while creating account.",
      });
    }
  };

  // small helper to render error under inputs
  const ErrorText = ({ field }) =>
    errors[field] ? (
      <p className="text-sm text-red-600 mt-1">{errors[field]}</p>
    ) : null;

  // ----------------- JSX + Styles -----------------
  return (
    <>
      <style>{`
        /* --- Modal styles (professional) --- */
        .modal-backdrop {
          position: fixed;
          inset: 0;
          background: rgba(10, 15, 20, 0.45);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
          padding: 20px;
        }
        .modal-card {
          width: 100%;
          max-width: 520px;
          background: #fff;
          border-radius: 12px;
          box-shadow: 0 10px 35px rgba(7,20,25,0.2);
          overflow: hidden;
          transform: translateY(8px);
          animation: modalPop .18s ease-out forwards;
        }
        @keyframes modalPop {
          from { opacity: 0; transform: translateY(12px) scale(.995); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        .modal-body {
          padding: 22px;
          display: flex;
          gap: 18px;
        }
        .modal-icon {
          min-width: 72px;
          min-height: 72px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 30px;
          color: #fff;
        }
        .modal-content {
          flex: 1;
          padding: 20px;
        }
        .modal-title {
          font-weight: 700;
          font-size: 1.05rem;
          margin-bottom: 6px;
          color: #0f1724;
        }
        .modal-message {
          color: #4b5563;
          line-height: 1.45;
          margin-bottom: 12px;
        }
        .modal-actions {
          display: flex;
          justify-content: flex-end;
          gap: 10px;
          padding: 14px 20px;
          border-top: 1px solid #eef2f6;
          background: #fafbfd;
        }
        .btn-modal {
          min-width: 92px;
          padding: 9px 14px;
          border-radius: 8px;
          font-weight: 600;
          border: none;
          cursor: pointer;
        }
        .btn-confirm {
          background: linear-gradient(90deg,#06b6a4,#06d1c6);
          color: #fff;
          box-shadow: 0 6px 20px rgba(6,182,164,0.12);
        }
        .btn-cancel {
          background: #fff;
          border: 1px solid #dbe7e6;
          color: #0f1724;
        }

        /* icon colors by variant */
        .ic-success { background: linear-gradient(180deg,#06d6a0,#05b389); height: fit-content; }
        .ic-warning { background: linear-gradient(90deg, #06b6a4, #06d1c6); }
        .ic-error   { background: linear-gradient(180deg,#ff6b6b,#ff4d4d); }
        .ic-info    { background: linear-gradient(180deg,#60a5fa,#3b82f6); }

        /* small responsive */
        @media (max-width: 480px) {
          .modal-body { flex-direction: column; align-items: center; text-align:center; }
          .modal-icon { margin-bottom: 6px; }
          .modal-actions { flex-direction: column-reverse; gap: 8px; padding: 12px; }
          .btn-modal { width: 100%; }
        }

        /* rest of your form helper styles (kept minimal) */
        .error-input { border-color: #dc2626 !important; box-shadow: none !important; }
        .required-star { color: #dc2626; margin-left: 4px; font-weight: 700; }

        /* --- FIX FOR PASSWORD ICON alignment --- */
        /* Ensure inputs have consistent height and line-height */
        .fixed-input {
          height: 44px;
          line-height: 1.15;
          padding: 10px 12px;
          box-sizing: border-box;
          border-radius: 8px;
          border: 1px solid rgba(2,6,23,0.08);
        }
        /* Icon wrapper placed absolute inside a relative parent,
           vertically centered with translateY(-50%) so it won't drift. */
        .icon-wrap {
          position: absolute;
           padding-bottom: 45px;
          right: 12px;
          transform: translateY(-50%);
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 34px;
          height: 34px;
          cursor: pointer;
          color: #0f1724;
        }
          .login-logo {
  max-width: 140px;
  transition: 0.3s ease;
   object-fit: contain;
}
.login-logo-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 16px;
  width: 100%;
}
.login-logo:hover {
  transform: scale(1.05);
}
      `}</style>

      {/* Modal Component */}
      {modal.open && (
        <div className="modal-backdrop" role="dialog" aria-modal="true">
          <div className="modal-card" role="document">
            <div className="modal-body">
              <div
                className={`modal-icon ${
                  modal.variant === "success"
                    ? "ic-success"
                    : modal.variant === "warning"
                    ? "ic-warning"
                    : modal.variant === "error"
                    ? "ic-error"
                    : "ic-info"
                }`}
                aria-hidden="true"
              >
                {modal.variant === "success" && <FaCheckCircle size={30} />}
                {modal.variant === "warning" && (
                  <FaExclamationTriangle size={30} />
                )}
                {modal.variant === "error" && <FaTimesCircle size={30} />}
                {modal.variant === "info" && <FaInfoCircle size={30} />}
              </div>

              <div className="modal-content">
                <div className="modal-title">{modal.title}</div>
                <div className="modal-message">{modal.message}</div>
              </div>
            </div>

            {/* <div className="modal-actions">
              {modal.showCancel && (
                <button
                  className="btn-modal btn-cancel"
                  onClick={() => {
                    if (typeof modal.onCancel === "function") modal.onCancel();
                    closeModal();
                  }}
                >
                  {modal.cancelText}
                </button>
              )}
              <button
                className="btn-modal btn-confirm"
                onClick={() => {
                  if (typeof modal.onConfirm === "function") modal.onConfirm();
                  else closeModal();
                }}
              >
                {modal.confirmText}
              </button>
            </div> */}
            <div className="modal-actions">
              {modal.showCancel && (
                <button
                  className="btn-modal btn-cancel"
                  onClick={() => {
                    if (typeof modal.onCancel === "function") modal.onCancel();
                    closeModal();
                  }}
                >
                  {modal.cancelText}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Main Registration Form (unchanged layout, using openModal instead of Swal) */}
      <div
        className="sign-in__wrapper"
        style={{
          background:
            "linear-gradient(135deg, #f0fdfa 0%, #ccfbf1 50%, #dbeafe 100%)",
          padding: "20px 0",
        }}
      >
        <form
          className="d-flex justify-content-center mt-5"
          onSubmit={(e) => e.preventDefault()}
        >
          <div
            className="card px-4 py-5 gps-card"
            style={{ width: "35rem", maxWidth: "90%" }}
            onKeyDown={handleGlobalEnterKey}
            tabIndex="0"
          >
            <div className="login-logo-wrapper">
              <Link to="/">
                <img
                  src="/images/gpslogo.png"
                  alt="GPS Logo"
                  className="login-logo"
                  style={{ cursor: "pointer" }}
                />
              </Link>
            </div>

            <div className="text-center mb-4">
              <h3 className="mt-3 text-gps-primary">Create Your Account</h3>
              <p
                className="text-gps-muted"
                style={{ fontSize: "1.1rem", lineHeight: "1.8", color: "#000" }}
              >
                Join GPS Global Portfolio Solutions
              </p>
            </div>

            {/* Step 1 */}
            {!showOTP && !showSetPassword && (
              <>
                <div className="mb-3">
                  <label className="form-label text-gps-primary">
                    <strong>
                      Account Type<span className="required-star">*</span>
                    </strong>
                  </label>
                  <select
                    className={`form-control ${
                      errors.role ? "error-input" : ""
                    } fixed-input`}
                    name="role"
                    value={data.role}
                    onChange={handleInputChange}
                    onBlur={() => {
                      const e = validateFieldSingle("role");
                      setErrors((prev) => ({ ...prev, ...e }));
                    }}
                  >
                    <option value="">Select Type</option>
                    <option value="2">Advisor</option>
                    <option value="3">Client</option>
                  </select>
                  <ErrorText field="role" />
                </div>

                <div className="mb-3">
                  <label className="form-label text-gps-primary">
                    <strong>
                      PAN No.<span className="required-star">*</span>
                    </strong>
                  </label>
                  <input
                    type="text"
                    className={`form-control ${
                      errors.pan_number ? "error-input" : ""
                    } fixed-input`}
                    name="pan_number"
                    value={data.pan_number}
                    onChange={handleInputChange}
                    onBlur={handlePanBlur}
                    placeholder="Enter Your PAN No."
                  />
                  <ErrorText field="pan_number" />
                </div>

                <div className="mb-3">
                  <label className="form-label text-gps-primary">
                    <strong>
                      First Name <span className="required-star">*</span>
                    </strong>
                  </label>
                  <input
                    type="text"
                    className={`form-control ${
                      errors.first_name ? "error-input" : ""
                    } fixed-input`}
                    name="first_name"
                    value={data.first_name}
                    onChange={handleInputChange}
                    onBlur={(e) => {
                      const err = validateFieldSingle(
                        "first_name",
                        e.target.value,
                      );
                      setErrors((prev) => ({ ...prev, ...err }));
                    }}
                    placeholder="Enter First Name"
                  />
                  <ErrorText field="first_name" />
                </div>

                <div className="mb-3">
                  <label className="form-label text-gps-primary">
                    <strong>
                      Last Name <span className="required-star">*</span>
                    </strong>
                  </label>
                  <input
                    type="text"
                    className={`form-control ${
                      errors.last_name ? "error-input" : ""
                    } fixed-input`}
                    name="last_name"
                    value={data.last_name}
                    onChange={handleInputChange}
                    onBlur={(e) => {
                      const err = validateFieldSingle(
                        "last_name",
                        e.target.value,
                      );
                      setErrors((prev) => ({ ...prev, ...err }));
                    }}
                    placeholder="Enter Last Name"
                  />
                  <ErrorText field="last_name" />
                </div>

                <div className="mb-3">
                  <label className="form-label text-gps-primary">
                    <strong>
                      Email Address <span className="required-star">*</span>
                    </strong>
                  </label>
                  <input
                    type="email"
                    className={`form-control ${
                      errors.email ? "error-input" : ""
                    } fixed-input`}
                    name="email"
                    value={data.email}
                    onChange={handleInputChange}
                    onBlur={(e) => {
                      const err = validateFieldSingle("email", e.target.value);
                      setErrors((prev) => ({ ...prev, ...err }));
                    }}
                    placeholder="Enter Your Email"
                  />
                  <ErrorText field="email" />
                </div>

                <div className="mb-3">
                  <label className="form-label text-gps-primary">
                    <strong>
                      Contact Number <span className="required-star">*</span>
                    </strong>
                  </label>
                  <div
                    style={{
                      position: "relative",
                      display: "flex",
                      gap: 8,
                      alignItems: "center",
                    }}
                  >
                    <select
                      name="country_code"
                      value={data.country_code}
                      onChange={handleInputChange}
                      className="form-control fixed-input"
                      style={{ maxWidth: "160px" }}
                    >
                      {countryCodes.map((c) => (
                        <option key={c.code} value={c.code}>
                          {c.label}
                        </option>
                      ))}
                    </select>
                    <input
                      type="tel"
                      className={`form-control ${
                        errors.phone ? "error-input" : ""
                      } fixed-input`}
                      name="phone"
                      value={data.phone}
                      onChange={handleInputChange}
                      onBlur={(e) => {
                        const err = validateFieldSingle(
                          "phone",
                          e.target.value,
                        );
                        setErrors((prev) => ({ ...prev, ...err }));
                      }}
                      placeholder="Enter phone digits only"
                      maxLength={data.country_code === "+91" ? 10 : 15}
                    />
                  </div>
                  <ErrorText field="phone" />
                </div>

                {data.role === "2" && (
                  <div className="mb-3">
                    <label className="form-label text-gps-primary">
                      <strong>
                        Upload NISM Certificate{" "}
                        <span className="required-star">*</span>
                      </strong>
                    </label>
                    <input
                      type="file"
                      className={`form-control ${
                        errors.document ? "error-input" : ""
                      }`}
                      name="document"
                      accept=".jpg,.jpeg,.png,.pdf"
                      onChange={handleInputChange}
                    />
                    <small className="text-gps-muted">
                      Allowed formats: JPG, PNG, PDF
                    </small>
                    <ErrorText field="document" />
                  </div>
                )}

                {data.role === "3" && (
                  <div className="mb-3">
                    <label className="form-label text-gps-primary">
                      <strong>Select Advisor (Optional)</strong>
                    </label>
                    {loadingAgents ? (
                      <div className="form-control fixed-input" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <span style={{ color: "#64748b" }}>Loading advisors...</span>
                      </div>
                    ) : (
                      <select
                        className={`form-control ${
                          errors.agent_id ? "error-input" : ""
                        } fixed-input`}
                        name="agent_id"
                        value={data.agent_id}
                        onChange={handleInputChange}
                      >
                        <option value="">Select Advisor (Optional)</option>
                        {agentsList.map((agent) => (
                          <option key={agent.id} value={agent.id}>
                            {agent.name} {agent.email ? `(${agent.email})` : ""}
                          </option>
                        ))}
                      </select>
                    )}
                    <small className="text-gps-muted">
                      You can select an advisor to be assigned to your account (optional)
                    </small>
                    <ErrorText field="agent_id" />
                  </div>
                )}

                <button
                  type="button"
                  onClick={(e) => handleSendOTP(e)}
                  className="btn btn-gps-primary w-100 py-2"
                >
                  Send OTP
                </button>

                <div className="text-center">
                  <p className="mb-2 mt-3">Have an account?</p>
                  <Link
                    to={"/login"}
                    onClick={() =>
                      window.scrollTo({ top: 0, behavior: "instant" })
                    }
                  >
                    <button type="button" className="btn btn-gps-primary">
                      Log In
                    </button>
                  </Link>
                </div>
              </>
            )}

            {/* Step 2: Verify OTP */}
            {showOTP && !showSetPassword && (
              <div className="text-center p-4">
                <h4 className="text-gps-primary mb-3">Verify Your Account</h4>
                <p style={{ color: "#000" }}>
                  We've sent a 4-digit OTP to your email
                </p>

                <div className="mb-4 d-flex justify-content-center gap-3">
                  {otpDigits.map((digit, index) => (
                    <input
                      key={index}
                      type="text"
                      maxLength="1"
                      className="form-control text-center"
                      value={digit}
                      onChange={(e) => handleOtpDigitChange(e.target, index)}
                      onKeyDown={(e) => handleOtpKeyDown(e, index)}
                      onPaste={(e) => handleOtpPaste(e, index)} 
                      ref={(el) => (inputRefs.current[index] = el)}
                      style={{
                        padding: "0",
                        width: "40px",
                        height: "50px",
                        fontSize: "24px",
                        borderRadius: "8px",
                        border: "1px solid #06d1c6",
                        color: "#000",
                        backgroundColor: "#fff",
                        caretColor: "#06d1c6",
                      }}
                    />
                  ))}
                </div>

                <button
                  type="button"
                  onClick={handleVerifyOTP}
                  className="btn btn-gps-primary w-100 py-2"
                  style={{ color: "#fff" }}
                >
                  Verify OTP
                </button>
              </div>
            )}

            {/* Step 3: Set Password */}
            {showSetPassword && (
              <form onSubmit={(e) => e.preventDefault()}>
                <div style={{ position: "relative" }} className="mb-3">
                  <label className="form-label text-gps-primary">
                    <strong>Password *</strong>
                  </label>
                  <input
                    type={data.showPassword ? "text" : "password"}
                    className={`form-control ${
                      errors.password ? "error-input" : ""
                    } fixed-input`}
                    name="password"
                    value={data.password}
                    onChange={(e) => {
                      handleInputChange(e);
                      const err = validateFieldSingle(
                        "password",
                        e.target.value,
                      );
                      setErrors((prev) => ({ ...prev, ...err }));
                    }}
                    placeholder="Enter Your Password"
                  />
                  {/* ICON: use .icon-wrap which is vertically centered */}
                  <span
                    className="icon-wrap"
                    onClick={() =>
                      setData((p) => ({ ...p, showPassword: !p.showPassword }))
                    }
                    aria-hidden
                  >
                    {data.showPassword ? <FaEyeSlash /> : <FaEye />}
                  </span>
                  <ErrorText field="password" />
                </div>

                <div style={{ position: "relative" }} className="mb-3">
                  <label className="form-label text-gps-primary">
                    <strong>Confirm Password *</strong>
                  </label>
                  <input
                    type={data.showConfirmPassword ? "text" : "password"}
                    className={`form-control ${
                      errors.confirmPassword ? "error-input" : ""
                    } fixed-input`}
                    name="confirmPassword"
                    value={data.confirmPassword}
                    onChange={(e) => {
                      handleInputChange(e);
                      const err = validateFieldSingle(
                        "confirmPassword",
                        e.target.value,
                      );
                      setErrors((prev) => ({ ...prev, ...err }));
                    }}
                    placeholder="Confirm Your Password"
                  />
                  <span
                    className="icon-wrap"
                    onClick={() =>
                      setData((p) => ({
                        ...p,
                        showConfirmPassword: !p.showConfirmPassword,
                      }))
                    }
                    aria-hidden
                  >
                    {data.showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                  </span>
                  <ErrorText field="confirmPassword" />
                </div>

                <div className="form-check mb-3">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="termsCheckbox1"
                    name="acceptTerms"
                    checked={data.acceptTerms || false}
                    onChange={handleInputChange}
                  />
                  <label
                    className="form-check-label"
                    htmlFor="termsCheckbox1"
                    style={{ fontSize: "0.9rem", color: "#000" }}
                  >
                    I Accept the{" "}
                    <a
                      href="/termsandconditions"
                      target="_blank"
                      rel="noreferrer"
                    >
                      <b style={{ color: "#1bafb4" }}>Terms & Conditions</b>
                    </a>{" "}
                    &{" "}
                    <a href="/PrivacyPolicy" target="_blank" rel="noreferrer">
                      <b style={{ color: "#1bafb4" }}>Privacy Policy</b>
                    </a>
                  </label>
                </div>

                <button
                  type="button"
                  onClick={handleCreateAccount}
                  className="btn btn-gps-primary w-100 py-2"
                >
                  Create Account
                </button>
              </form>
            )}
          </div>
        </form>
      </div>
    </>
  );
}
